import { useEffect, useLayoutEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getActionCableConsumer } from "services/actionCable";

type DetailQueryKey = readonly unknown[];

interface UsePlatformEventCableArgs {
  entityType?: string | null;
  entityId?: string | null;
  enabled?: boolean;
  detailQueryKeys?: DetailQueryKey | DetailQueryKey[];
}

export default function usePlatformEventCable({
  entityType,
  entityId,
  enabled = true,
  detailQueryKeys,
}: UsePlatformEventCableArgs) {
  const queryClient = useQueryClient();
  const detailKeysRef = useRef<DetailQueryKey[]>([]);

  // Serialize for stable dep comparison — React bails on the effect when content
  // is unchanged, so callers don't need to memoize detailQueryKeys. useLayoutEffect
  // (not useEffect) closes the race window where a WebSocket message could arrive
  // between render and effect and read stale keys.
  const serialized = JSON.stringify(detailQueryKeys);
  useLayoutEffect(() => {
    if (!detailQueryKeys) {
      detailKeysRef.current = [];
    } else {
      detailKeysRef.current = Array.isArray(detailQueryKeys[0])
        ? (detailQueryKeys as DetailQueryKey[])
        : [detailQueryKeys as DetailQueryKey];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);

  useEffect(() => {
    if (!enabled || !entityType || !entityId) return undefined;

    const consumer = getActionCableConsumer();
    const subscription = consumer.subscriptions.create(
      { channel: "PlatformEventsChannel" },
      {
        received: (payload: { entityType?: string; entityId?: string }) => {
          if (payload.entityType !== entityType || String(payload.entityId) !== String(entityId)) return;

          queryClient.invalidateQueries({ queryKey: ["platformEvents", "list"] });
          detailKeysRef.current.forEach((queryKey) => {
            queryClient.invalidateQueries({ queryKey });
          });
        },
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [enabled, entityId, entityType, queryClient]);
}
